import type { CostHour, CostMonth, CostResponse } from '../api/contracts';

/**
 * Pure cents->dollars transforms for CostResponse. Deliberately has no
 * dependency on apiClient/react-query/auth so these can be unit tested
 * without bootstrapping the app's auth config (see auth/config.ts, which
 * reads `window.location` at module-load time and cannot run outside a DOM
 * environment). useCost.ts re-exports everything here for page consumers.
 *
 * See api/src/lib/tariff.ts's header comment (conventions 4 and 7) for why
 * `fixedCents`/`percentCents` only ever appear at the month/total level and
 * never inside `byHour`: a flat monthly fee or bill-level percentage rider
 * can't be attributed to a single hour's kWh without distorting that hour's
 * real marginal cost. Every function below respects that split -- none of
 * them compute or expose a per-hour fixed/percent number.
 */

const centsToDollars = (cents: number): number => cents / 100;

/** Dollar-converted totals for the range, split by charge KIND (not just a single number). */
export interface CostSummaryDollars {
  totalKwh: number;
  energyDollars: number;
  fixedDollars: number;
  percentDollars: number;
  totalDollars: number;
}

/**
 * Converts the range-level totals to dollars. This is the ONLY place fixed
 * and percent charges are surfaced outside the monthly table -- deliberately
 * at the total/range level, never per hour.
 */
export function toCostSummaryDollars(
  cost: Pick<CostResponse, 'totalKwh' | 'energyCents' | 'fixedCents' | 'percentCents' | 'totalCents'>,
): CostSummaryDollars {
  return {
    totalKwh: cost.totalKwh,
    energyDollars: centsToDollars(cost.energyCents),
    fixedDollars: centsToDollars(cost.fixedCents),
    percentDollars: centsToDollars(cost.percentCents),
    totalDollars: centsToDollars(cost.totalCents),
  };
}

/** Dollar-converted row for the monthly breakdown table -- the only place fixed/percent land per period. */
export interface CostMonthRow {
  key: string;
  year: number;
  month: number;
  kwh: number;
  daysPresent: number;
  energyDollars: number;
  fixedDollars: number;
  percentDollars: number;
  totalDollars: number;
}

export function toMonthRows(months: readonly CostMonth[]): CostMonthRow[] {
  return months.map((m) => ({
    key: `${m.year}-${String(m.month).padStart(2, '0')}`,
    year: m.year,
    month: m.month,
    kwh: m.kwh,
    daysPresent: m.daysPresent,
    energyDollars: centsToDollars(m.energyCents),
    fixedDollars: centsToDollars(m.fixedCents),
    percentDollars: centsToDollars(m.percentCents),
    totalDollars: centsToDollars(m.totalCents),
  }));
}

/**
 * Hourly ENERGY-ONLY cost point for the chart. Deliberately has no
 * fixed/percent field -- `CostHour` from the API never carries one, and this
 * function must not invent one by dividing the month/total fixed or percent
 * charge across hours (see tariff.ts convention 4: that would make low-usage
 * hours look artificially cheap and high-usage hours artificially expensive
 * relative to their real marginal cost).
 */
export interface HourlyEnergyPoint {
  x: Date;
  kwh: number;
  energyDollars: number;
}

export function toHourlyEnergyPoints(byHour: readonly CostHour[]): HourlyEnergyPoint[] {
  return byHour.map((h) => ({
    x: new Date(h.intervalStart),
    kwh: h.kwh,
    energyDollars: centsToDollars(h.energyCents),
  }));
}
